import React, { useContext, useEffect, useState } from "react";
import axios from "v2/utils/axios";

interface Freshness {
	version: number;
	report: (generatedAt: string) => void;
}

export const DashboardFreshness = React.createContext<Freshness>({ version: 0, report: () => undefined });

interface Result<T> {
	data: T | null;
	isLoading: boolean;
	error: boolean;
}

interface Options {
	background?: boolean;
	enabled?: boolean;
}

let primaryInFlight = 0;
let backgroundRunning = false;
const waiting: (() => void)[] = [];

const pump = () => {
	if (primaryInFlight > 0 || backgroundRunning) return;
	const next = waiting.shift();
	if (next) next();
};

export function useDashboardData<T>(
	endpoint: string,
	params: Record<string, string | number | undefined>,
	{ background = false, enabled = true }: Options = {}
): Result<T> {
	const [data, setData] = useState<T | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const { version, report } = useContext(DashboardFreshness);
	const key = JSON.stringify(params);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		setIsLoading(true);
		setError(false);
		const query: Record<string, string | number> = {};
		Object.keys(params).forEach((name) => {
			const value = params[name];
			if (value !== undefined && value !== "") query[name] = value;
		});

		const request = () =>
			axios
				.get(`${process.env.REACT_APP_API_ENDPOINT_V2}executive_dashboard/${endpoint}`, { params: query })
				.then((res) => {
					if (cancelled) return;
					setData((res.data && res.data.data) as T);
					if (res.data && res.data.generated_at) report(res.data.generated_at);
				})
				.catch(() => {
					if (!cancelled) setError(true);
				})
				.finally(() => {
					if (!cancelled) setIsLoading(false);
				});

		if (!background) {
			primaryInFlight += 1;
			request().finally(() => {
				primaryInFlight -= 1;
				pump();
			});
			return () => {
				cancelled = true;
			};
		}

		const start = () => {
			backgroundRunning = true;
			request().finally(() => {
				backgroundRunning = false;
				pump();
			});
		};
		waiting.push(start);
		pump();
		return () => {
			cancelled = true;
			const index = waiting.indexOf(start);
			if (index >= 0) waiting.splice(index, 1);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [endpoint, key, enabled, version]);

	return { data, isLoading, error };
}
